import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize } from "lucide-react";
import {
  Box,
  Stack,
  IconButton,
  Slider,
  Typography,
} from "@mui/material";

interface CustomVideoPlayerProps {
  src: string;
}

const CustomVideoPlayer: React.FC<CustomVideoPlayerProps> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
  
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => setIsPlaying(true)).catch(() => {
          setIsPlaying(false);
        });
      } else {
        setIsPlaying(true);
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const newVolume = Array.isArray(value) ? value[0] : value;
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      const isMuted = newVolume === 0;
      setIsMuted(isMuted);
      videoRef.current.muted = isMuted;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const newMuted = !video.muted;
      video.muted = newMuted;
      setIsMuted(newMuted);

      if (newMuted) {
        setVolume(0);
      } else {
        if (video.volume === 0) {
          video.volume = 1;
        }
        setVolume(video.volume);
      }
    }
  };

  const handleProgressChange = (_: Event, value: number | number[]) => {
    if (videoRef.current) {
      const newTime = Array.isArray(value) ? value[0] : value;
      videoRef.current.currentTime = newTime;
      setProgress(newTime);
    }
  };

  const toggleFullscreen = () => {
    if (!playerRef.current) return;

    if (!document.fullscreenElement) {
      playerRef.current.requestFullscreen().catch((err) => {
        alert(
          `Error attempting to enable full-screen mode: ${err.message} (${err.name})`
        );
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = false;
    v.volume = 1;
    setIsMuted(false);
    setVolume(v.volume);
  
    const updateProgress = () => setProgress(v.currentTime);
    const setVideoDuration = () => setDuration(v.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
  
    v.addEventListener("timeupdate", updateProgress);
    v.addEventListener("loadedmetadata", setVideoDuration);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
  
    return () => {
      v.removeEventListener("timeupdate", updateProgress);
      v.removeEventListener("loadedmetadata", setVideoDuration);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      try { v.pause(); } catch (error) {
        console.error("Error while pausing the video:", error);
      }
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      togglePlayPause();
    }
  }, [videoRef]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <Box
      ref={playerRef}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        bgcolor: "black",
        overflow: "hidden",
        "&:hover .controls": { opacity: 1 },
      }}
    >
      <video
        ref={videoRef}
        onClick={togglePlayPause}
        playsInline
        autoPlay
        muted
        preload="metadata"
        style={{ width: "100%", height: "100%", display: "block", cursor: "pointer" }}
        onError={(e) => {
          const v = e.currentTarget;
          console.error("Video error", v.error);
        }}
      >
        <source src={src} type="video/mp4" />
        </video>

      <Box
        className="controls"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          p: 2,
          pt: 4,
          opacity: 0,
          transition: "opacity 300ms",
          backgroundImage:
            "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))",
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" sx={{ color: "#fff", fontFamily: "monospace" }}>
              {formatTime(progress)}
            </Typography>
            <Slider
              min={0}
              max={Math.max(duration, 0)}
              step={1}
              value={Math.min(progress, duration || 0)}
              onChange={handleProgressChange}
              sx={{
                color: "#104145",
                flex: 1,
                "& .MuiSlider-thumb": { width: 12, height: 12 },
              }}
            />
            <Typography variant="caption" sx={{ color: "#fff", fontFamily: "monospace" }}>
              {formatTime(duration)}
            </Typography>
          </Stack>

          {/* Controls */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {/* Left controls */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                onClick={togglePlayPause}
                size="small"
                sx={{
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </IconButton>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 160 }}>
                <IconButton
                  onClick={toggleMute}
                  size="small"
                  sx={{
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                  }}
                >
                  {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </IconButton>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  value={volume}
                  onChange={handleVolumeChange}
                  sx={{
                    color: "#104145",
                    "& .MuiSlider-thumb": { width: 12, height: 12 },
                  }}
                />
              </Stack>
            </Stack>

            {/* Right controls */}
            <IconButton
              onClick={toggleFullscreen}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default CustomVideoPlayer;